// frontend/src/contexts/TimerContext.js
import { createContext, useContext, useState } from 'react';

const TimerContext = createContext(null);

const STORAGE_KEY = 'lawliet_timer';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// timerState shape: { dossier_id, dossier_title, started_at (ISO), paused_at (ISO|null) }

export function TimerProvider({ children }) {
  const [timerState, setTimerState] = useState(() => loadFromStorage());

  const start = (dossier_id, dossier_title) => {
    const state = {
      dossier_id,
      dossier_title,
      started_at: new Date().toISOString(),
      paused_at: null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    setTimerState(state);
  };

  const pause = () => {
    if (!timerState || timerState.paused_at) return;
    const updated = { ...timerState, paused_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setTimerState(updated);
  };

  const resume = () => {
    if (!timerState?.paused_at) return;
    const pausedMs = new Date() - new Date(timerState.paused_at);
    const newStartedAt = new Date(new Date(timerState.started_at).getTime() + pausedMs).toISOString();
    const updated = { ...timerState, started_at: newStartedAt, paused_at: null };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setTimerState(updated);
  };

  const stop = () => {
    localStorage.removeItem(STORAGE_KEY);
    setTimerState(null);
  };

  const getElapsedMinutes = () => {
    if (!timerState) return 0;
    const ref = timerState.paused_at ? new Date(timerState.paused_at) : new Date();
    return Math.floor((ref - new Date(timerState.started_at)) / 60000);
  };

  return (
    <TimerContext.Provider value={{ timerState, start, pause, resume, stop, getElapsedMinutes }}>
      {children}
    </TimerContext.Provider>
  );
}

export const useTimer = () => useContext(TimerContext);
